import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Slider,
} from '@mui/material';
import Cropper from 'react-easy-crop';
import { showToast } from '../CoreDataService';

/**
 * BannerImageCropper Component
 * 
 * A modal dialog that allows users to crop images with a 5:1 aspect ratio
 * specifically designed for banner advertisements.
 * 
 * @param {boolean} open - Controls the visibility of the cropper dialog
 * @param {function} onClose - Callback when the dialog is closed
 * @param {string} imageSrc - The source URL of the image to crop
 * @param {function} onCropComplete - Callback with the cropped image file
 */
const BannerImageCropper = ({ open, onClose, imageSrc, onCropComplete }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Callback when crop area changes
  const onCropChange = (location) => {
    setCrop(location);
  };

  // Callback when zoom changes
  const onZoomChange = (zoom) => {
    setZoom(zoom);
  };

  // Callback when crop is complete
  const onCropCompleteCallback = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  /**
   * Creates a cropped image from the canvas
   * @param {string} imageSrc - Source image URL
   * @param {object} pixelCrop - Crop area in pixels
   * @returns {Promise<Blob>} - Cropped image as blob
   */
  const createCroppedImage = async (imageSrc, pixelCrop) => {
    const image = new Image();
    image.src = imageSrc;
    
    return new Promise((resolve, reject) => {
      image.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size to cropped area
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        // Draw the cropped image
        ctx.drawImage(
          image,
          pixelCrop.x,
          pixelCrop.y,
          pixelCrop.width,
          pixelCrop.height,
          0,
          0,
          pixelCrop.width,
          pixelCrop.height
        );

        // Convert canvas to blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Canvas is empty'));
              return;
            }
            resolve(blob);
          },
          'image/jpeg',
          0.95
        );
      };

      image.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    });
  };

  /**
   * Handles the crop confirmation
   */
  const handleCropConfirm = async () => {
    if (!croppedAreaPixels) {
      showToast('error', 'Please adjust the crop area');
      return;
    }

    setIsProcessing(true);

    try {
      // Create cropped image blob
      const croppedImageBlob = await createCroppedImage(imageSrc, croppedAreaPixels);
      
      // Convert blob to file
      const croppedImageFile = new File(
        [croppedImageBlob],
        `banner-${Date.now()}.jpg`,
        { type: 'image/jpeg' }
      );

      // Pass the cropped file back to parent
      onCropComplete(croppedImageFile);
      
      // Reset state
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      
      showToast('success', 'Image cropped successfully');
      onClose();
    } catch (error) {
      console.error('Error cropping image:', error);
      showToast('error', 'Failed to crop image. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  /**
   * Handles dialog close
   */
  const handleClose = () => {
    if (!isProcessing) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isProcessing}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Crop Banner Image
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Adjust the image to fit the 5:1 aspect ratio for your banner ad
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ position: 'relative', height: 400, backgroundColor: '#000' }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={5 / 1}
            onCropChange={onCropChange}
            onZoomChange={onZoomChange}
            onCropComplete={onCropCompleteCallback}
            style={{
              containerStyle: {
                backgroundColor: '#000',
              },
            }}
          />
        </Box>

        <Box sx={{ mt: 3, px: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Zoom
          </Typography>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, value) => onZoomChange(value)}
            sx={{ mt: 1 }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            💡 Tip: Use the slider to zoom in/out and drag the image to reposition
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isProcessing}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCropConfirm}
          variant="contained"
          disabled={isProcessing}
          sx={{ textTransform: 'none', minWidth: 100 }}
        >
          {isProcessing ? 'Processing...' : 'Apply Crop'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BannerImageCropper;